import React, { useState, useEffect } from 'react';
import { Search, Download, Eye, Edit2, Trash2, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ViewNews = () => {
  const navigate = useNavigate();
  const [newsItems, setNewsItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // mock data
  const sampleNews = [
    {
      id: 'NEWS-001',
      title: 'Cooperative Launches New Product',
      author: 'Admin',
      date: '2026-02-25',
      status: 'Published',
      snippet: 'Our cooperative has just introduced a new line of organic products...'
    },
    {
      id: 'NEWS-002',
      title: 'Annual Meeting Scheduled',
      author: 'Secretary',
      date: '2026-01-10',
      status: 'Published',
      snippet: 'The annual general meeting will take place on March 1st...'
    },
    {
      id: 'NEWS-003',
      title: 'Member Spotlight: Aisha',
      author: 'Editor',
      date: '2025-12-20',
      status: 'Draft',
      snippet: 'This month we highlight one of our longest-serving members...'
    }
  ];

  useEffect(() => {
    const t = setTimeout(() => {
      setNewsItems(sampleNews);
      setLoading(false);
    }, 500);
    return () => clearTimeout(t);
  }, []);

  const filtered = newsItems.filter(n =>
    n.title.toLowerCase().includes(search.toLowerCase()) ||
    n.author.toLowerCase().includes(search.toLowerCase()) ||
    n.id.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (d) => {
    try {
      return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return d;
    }
  };

  const exportToCSV = () => {
    try {
      const headers = ['ID','Title','Author','Date','Status','Snippet'];
      const rows = newsItems.map(n => [
        n.id,
        `"${n.title}"`,
        n.author,
        n.date,
        n.status,
        `"${n.snippet}"`
      ].join(','));
      const csv = [headers.join(','), ...rows].join('\n');
      const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `news_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('export error', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 ml-8 lg:ml-24">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">News</h1>
            <p className="text-sm text-gray-500">Latest cooperative news & announcements</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/addnews')} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg">
              <Plus size={16}/> Add News
            </button>
            <button onClick={exportToCSV} className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg"><Download size={16}/> Export</button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow border border-gray-100 p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by title, author or ID"
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
            />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="min-w-full divide-y text-left">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-xs font-semibold text-gray-700">ID</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-700">Title</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-700">Author</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-700">Date</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-700">Status</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-400">Loading news...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-400">No news items found.</td></tr>
              ) : (
                filtered.map(n => (
                  <tr key={n.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-mono text-sm">{n.id}</td>
                    <td className="px-6 py-4">
                      <div className="font-medium">{n.title}</div>
                      <div className="text-xs text-gray-500 truncate max-w-xs">{n.snippet}</div>
                    </td>
                    <td className="px-6 py-4">{n.author}</td>
                    <td className="px-6 py-4">{formatDate(n.date)}</td>
                    <td className="px-6 py-4">{n.status}</td>
                    <td className="px-6 py-4 flex gap-2">
                      <button className="text-blue-600 p-2 rounded hover:bg-blue-50"><Eye size={16}/></button>
                      <button className="text-green-600 p-2 rounded hover:bg-green-50"><Edit2 size={16}/></button>
                      <button className="text-red-600 p-2 rounded hover:bg-red-50"><Trash2 size={16}/></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ViewNews;